/**
 * Gateway base URL for trendy microservices.
 * ASSUMPTION: REACT_APP_API_URL is the gateway origin (no trailing slash).
 * Falls back to the documented production gateway.
 */
const DEFAULT_GATEWAY = "https://trendy-gateway.titamedia.xyz";

const stripQuotes = (value: string): string =>
  value.trim().replace(/^['"]|['"]$/g, "").trim();

export const getGatewayBaseUrl = (): string => {
  const fromEnv = stripQuotes(process.env.REACT_APP_API_URL ?? "");
  const base = fromEnv || DEFAULT_GATEWAY;
  return base.replace(/\/+$/, "");
};

/** Join gateway origin with an absolute path (e.g. `/order-backend/...`). */
export const gatewayUrl = (path: string): string => {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${getGatewayBaseUrl()}${normalized}`;
};

/** trendy-order-service via gateway */
export const orderBackendUrl = {
  listOrders: (): string =>
    gatewayUrl("/order-backend/order/list-orders"),
  reprocess: (): string =>
    gatewayUrl("/order-backend/order/reprocess"),
};

/** trendy-dashboard-service via gateway */
export const dashboardBackendUrl = {
  logLook: (): string =>
    gatewayUrl("/dashboard-backend/dash/v1/log/look"),
  logFilter: (): string =>
    gatewayUrl("/dashboard-backend/dash/v1/log/filter"),
  logProcess: (): string =>
    gatewayUrl("/dashboard-backend/dash/v1/log/process"),
  inventoryMovements: (page: number, size: number): string =>
    gatewayUrl(
      `/dashboard-backend/api/dash/mov?page=${page}&size=${size}`
    ),
  priceMovements: (page: number, size: number): string =>
    gatewayUrl(
      `/dashboard-backend/api/dash/price-movements?page=${page}&size=${size}`
    ),
};

export const inventoryBackendUrl = {
  synchronize: (): string =>
    gatewayUrl("/inventory-backend/api/v1/processInventory"),
};

export const priceBackendUrl = {
  synchronize: (): string =>
    gatewayUrl("/price-backend/api/v1/price/synchronize"),
};

const DEFAULT_VTEX_ADMIN_ORDERS =
  "https://b2ctrendy.myvtex.com/admin/orders";

/**
 * VTEX Admin order detail URL.
 * Env: REACT_APP_VTEX_ADMIN_ORDERS_BASE (no trailing order id).
 */
export const getVtexAdminOrderUrl = (orderVTEXId: string): string => {
  const fromEnv = stripQuotes(
    process.env.REACT_APP_VTEX_ADMIN_ORDERS_BASE ?? ""
  );
  const base = (fromEnv || DEFAULT_VTEX_ADMIN_ORDERS).replace(/\/+$/, "");
  const id = orderVTEXId.trim();
  return `${base}/${encodeURIComponent(id)}`;
};
